import { type FC, useEffect, useRef } from 'react';
import cn from 'classnames';

import { getRewardItemInfo } from '@/common/config/daily-reward-config';
import { convertRemToPixels } from '@/common/lib/convertRemtoPx';
import { type Reward } from '@/common/types/earnRewardsType';

import RewardDayItem from './RewardDayItem';

interface RewardDayItemProps {
  rewards?: Reward[];
  activeReward?: Reward;
  className?: string;
}

const RewardsList: FC<RewardDayItemProps> = ({
  activeReward,
  rewards,
  className,
}) => {
  const activeRef = useRef<HTMLDivElement>(null);

  const getStatus = (reward: Reward): 'claimed' | 'future' => {
    if (reward.claimAt) return 'claimed';

    return 'future';
  };

  useEffect(() => {
    if (activeRef.current) {
      const element = activeRef.current;
      const rect = element.getBoundingClientRect();
      const isVisible =
        rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) -
          convertRemToPixels(5);

      if (!isVisible) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeReward]);

  return (
    <div
      className={cn('pt-4 px-8 gap-2 grid grid-cols-4 self-stretch', className)}
    >
      {rewards?.map((reward, i) => {
        const {
          img: { png, webp },
          amount,
          type,
        } = getRewardItemInfo(reward);

        const isActive = reward.rewardId === activeReward?.rewardId;

        return (
          <RewardDayItem
            day={i}
            status={isActive ? 'current' : getStatus(reward)}
            value={amount}
            variant={type}
            png={png}
            webp={webp}
            ref={isActive ? activeRef : undefined}
            key={reward.rewardId}
          />
        );
      })}
    </div>
  );
};

export default RewardsList;
